import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { apiClient } from '../../api/client'

interface Metrics {
  sessions_by_day: number[]
  locked_notes_by_day: number[]
  exports_by_day: number[]
  errors_by_day: number[]
  // BEGIN PLATFORM STAGE 4
  exports_ever?: boolean
  last_export_at?: string | null
  cpt_configured?: boolean
  // END PLATFORM STAGE 4
  // BEGIN PLATFORM STAGE 5
  unlocked_notes_over_48h_count?: number
  lock_rate_7d?: number
  missing_signatures_count?: number
  // END PLATFORM STAGE 5
  // BEGIN PLATFORM STAGE 6
  audit_events_7d?: number
  last_admin_action_at?: string | null
  last_platform_action_at?: string | null
  // END PLATFORM STAGE 6
  // BEGIN PLATFORM STAGE 7
  needs_support?: boolean
  internal_notes?: string | null
  tags?: string[]
  // END PLATFORM STAGE 7
  // BEGIN PLATFORM STAGE 8
  feature_flags?: Record<string, boolean>
  // END PLATFORM STAGE 8
  missing_sources?: string[]
}

export default function PlatformTenantDetailPage() {
  const { tenantId } = useParams()
  const [metrics, setMetrics] = useState<Metrics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [needsSupport, setNeedsSupport] = useState(false)
  const [internalNotes, setInternalNotes] = useState('')
  const [tags, setTags] = useState('')
  const [saving, setSaving] = useState(false)
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (!tenantId) return
    const load = async () => {
      try {
        setLoading(true)
        const res = await apiClient.get(`/api/platform/tenants/${tenantId}/metrics/`)
        setMetrics(res.data)
        setNeedsSupport(Boolean(res.data.needs_support))
        setInternalNotes(res.data.internal_notes || '')
        setTags((res.data.tags || []).join(', '))
        setFeatureFlags(res.data.feature_flags || {})
      } catch (e) {
        setError('Unable to load tenant metrics')
      } finally {
        setLoading(false)
      }
    }
    void load()
  }, [tenantId])

  const renderSeries = (label: string, data: number[] | undefined, color: string) => {
    if (!data) return null
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
        <div className="text-sm font-semibold text-slate-700 mb-2">{label}</div>
        <div className="flex items-end gap-1 h-32">
          {data.map((v, idx) => (
            <div
              key={idx}
              className={color}
              style={{ height: `${Math.min(100, v * 4)}%`, minWidth: '8px' }}
              title={`Day ${idx + 1}: ${v}`}
            />
          ))}
        </div>
      </div>
    )
  }

  if (loading) return <div className="p-8 text-gray-600">Loading tenant metrics�</div>
  if (error) return <div className="p-8 text-red-600">{error}</div>
  if (!metrics) return null

  const handleSaveOps = async () => {
    if (!tenantId) return
    try {
      setSaving(true)
      await apiClient.patch(`/api/platform/tenants/${tenantId}/ops/`, {
        needs_support: needsSupport,
        internal_notes: internalNotes,
        tags: tags.split(',').map((t) => t.trim()).filter(Boolean),
      })
      await apiClient.patch(`/api/platform/tenants/${tenantId}/features/`, {
        feature_flags: featureFlags,
      })
      // optimistic: already in local state
    } catch (e) {
      setError('Unable to save support notes')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="border-b bg-white">
        <div className="mx-auto max-w-6xl px-6 py-6 flex items-center gap-3">
          <Link to="/platform" className="text-sm text-primary-600 hover:underline">
            ? Back to tenants
          </Link>
          <h1 className="text-xl font-semibold text-slate-900">Tenant Metrics (last 30 days)</h1>
        </div>
      </div>

      <div className="mx-auto max-w-6xl px-6 py-6 space-y-4">
        {/* BEGIN PLATFORM STAGE 4 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-slate-500">CPT Configured</div>
            <div className="text-lg font-semibold text-slate-900">{metrics.cpt_configured ? 'Yes' : 'No'}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-slate-500">Exports Ever</div>
            <div className="text-lg font-semibold text-slate-900">{metrics.exports_ever ? 'Yes' : 'No'}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-slate-500">Last Export At</div>
            <div className="text-lg font-semibold text-slate-900">
              {metrics.last_export_at ? new Date(metrics.last_export_at).toLocaleString() : 'n/a'}
            </div>
          </div>
        </div>
        {/* END PLATFORM STAGE 4 */}
        {/* BEGIN PLATFORM STAGE 5 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-slate-500">Unlocked &gt;48h</div>
            <div className="text-lg font-semibold text-slate-900">
              {metrics.unlocked_notes_over_48h_count ?? 0}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-slate-500">Lock rate (7d)</div>
            <div className="text-lg font-semibold text-slate-900">
              {Math.round((metrics.lock_rate_7d ?? 0) * 100)}%
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-slate-500">Missing signatures</div>
            <div className="text-lg font-semibold text-slate-900">
              {metrics.missing_signatures_count ?? 0}
            </div>
          </div>
        </div>
        {/* END PLATFORM STAGE 5 */}
        {/* BEGIN PLATFORM STAGE 6 */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-slate-500">Audit events (7d)</div>
            <div className="text-lg font-semibold text-slate-900">{metrics.audit_events_7d ?? 0}</div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-slate-500">Last admin action</div>
            <div className="text-lg font-semibold text-slate-900">
              {metrics.last_admin_action_at ? new Date(metrics.last_admin_action_at).toLocaleString() : 'n/a'}
            </div>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-sm text-slate-500">Last platform action</div>
            <div className="text-lg font-semibold text-slate-900">
              {metrics.last_platform_action_at ? new Date(metrics.last_platform_action_at).toLocaleString() : 'n/a'}
            </div>
          </div>
        </div>
        {/* END PLATFORM STAGE 6 */}
        {/* BEGIN PLATFORM STAGE 7 */}
        <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-sm text-slate-500">Support state</div>
              <div className="text-lg font-semibold text-slate-900">
                {needsSupport ? 'Needs support' : 'Normal'}
              </div>
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={needsSupport}
                onChange={(e) => setNeedsSupport(e.target.checked)}
              />
              Mark as needs support
            </label>
          </div>
          <div>
            <div className="text-sm text-slate-500 mb-1">Internal notes (owner-only)</div>
            <textarea
              className="w-full rounded border border-slate-200 p-2 text-sm"
              rows={3}
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="No PHI. Support/internal ops notes only."
            />
          </div>
          <div>
          <div className="text-sm text-slate-500 mb-1">Tags (comma-separated)</div>
          <input
            className="w-full rounded border border-slate-200 p-2 text-sm"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="pilot, paused, support"
          />
        </div>
        <div>
          <div className="text-sm text-slate-500 mb-1">Feature flags (comma-separated key:true/false)</div>
          <textarea
            className="w-full rounded border border-slate-200 p-2 text-sm"
            rows={2}
            value={Object.entries(featureFlags).map(([k, v]) => `${k}:${v}`).join(', ')}
            onChange={(e) => {
              const entries = e.target.value.split(',').map((p) => p.trim()).filter(Boolean)
              const next: Record<string, boolean> = {}
              entries.forEach((pair) => {
                const [k, v] = pair.split(':').map((s) => s.trim())
                if (k) next[k] = v === 'true'
              })
              setFeatureFlags(next)
            }}
            placeholder="beta_dashboard:true, fast_export:false"
          />
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSaveOps}
              disabled={saving}
            >
              {saving ? 'Saving�' : 'Save support settings'}
            </button>
          </div>
        </div>
        {/* END PLATFORM STAGE 7 */}

        {renderSeries('Sessions', metrics.sessions_by_day, 'bg-blue-500')}
        {renderSeries('Locked Notes', metrics.locked_notes_by_day, 'bg-emerald-500')}
        {renderSeries('Exports', metrics.exports_by_day, 'bg-indigo-500')}
        {renderSeries('Errors', metrics.errors_by_day, 'bg-amber-500')}

        {metrics.missing_sources && metrics.missing_sources.length > 0 && (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-3">
            Missing data sources: {metrics.missing_sources.join(', ')}
          </div>
        )}
      </div>
    </div>
  )
}
